import { Currency } from '@procureai/schemas';
import { addYears, endOfYear } from 'date-fns';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import {
  SavingCostAllocationDistributionType,
  SavingReductionType,
  SavingStatus,
  SavingType,
} from '@/generated/gql/graphql';

const requiredTextSchema = z.string().trim().min(1);
const requiredComboboxValueSchema = z.string();
const requiredNonNegativeNumberSchema = z.number().min(0);
const optionalTrimmedStringSchema = z.string().nullable();

// A saving is long-term purely by virtue of carrying a validTo end date. When
// the user ticks the long-term checkbox we store an end-of-next-year timestamp:
// it lands in a later calendar year than the (current-year) validFrom, so the
// saving reads as long-term under both the legacy year-comparison rule and the
// new "validTo is set" rule while the backend change rolls out.
export const getLongTermValidTo = (): string => endOfYear(addYears(new Date(), 1)).toISOString();

const savingFormObjectSchema = z.object({
  purchaseRequisition: z.string(),
  shoppingCartId: z.string(),
  sourceReferenceItem: z.string(),
  name: requiredTextSchema,
  description: z.string(),
  currency: requiredTextSchema,
  status: z.enum(SavingStatus),
  savingType: z.enum(SavingType).nullable(),
  baselinePrice: requiredNonNegativeNumberSchema,
  newPrice: requiredNonNegativeNumberSchema,
  baselineQuantity: requiredNonNegativeNumberSchema,
  newQuantity: requiredNonNegativeNumberSchema,
  originalPriceQuantity: z.number().min(0).optional(),
  newPriceQuantity: z.number().min(0).optional(),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  procurementOrganizationId: requiredComboboxValueSchema,
  organizationId: requiredComboboxValueSchema,
  commodityGroupId: requiredComboboxValueSchema,
  commodity: z.string(),
  purchasingGroupId: requiredComboboxValueSchema,
  supplierId: requiredComboboxValueSchema,
  accountingAreaId: optionalTrimmedStringSchema,
  fkBuyer: optionalTrimmedStringSchema,
  unitId: optionalTrimmedStringSchema,
  glAccountId: optionalTrimmedStringSchema,
  accountAssignmentCategory: optionalTrimmedStringSchema,
  accountAssignmentValue: optionalTrimmedStringSchema,
  distributionType: z.enum(SavingCostAllocationDistributionType).nullable(),
  allocationValue: z.number().min(0).optional(),
  reductionType: z.enum(SavingReductionType).nullable(),
});

export const savingFormSchema = savingFormObjectSchema;

// On create the accounting area is mandatory: the backend derives the saving's
// access keys from it. On edit it is shown read-only and immutable (the update
// resolver ignores changes to it), so the base (optional) schema applies.
export const createSavingFormSchema = savingFormObjectSchema.extend({
  accountingAreaId: requiredComboboxValueSchema,
});

export type SavingFormData = z.infer<typeof savingFormObjectSchema>;

export const savingStatusOptions = Object.values(SavingStatus);

const pinnedCurrencies: Currency[] = [Currency.EUR, Currency.USD, Currency.GBP];
export const savingCurrencyOptions = [
  ...pinnedCurrencies,
  ...Object.values(Currency).filter((currency) => !pinnedCurrencies.includes(currency)),
];
export const savingTypeOptions = Object.values(SavingType);
export const savingReductionTypeOptions = Object.values(SavingReductionType);
export const savingCostAllocationDistributionTypeOptions = Object.values(SavingCostAllocationDistributionType);

export const savingFormDefaultValues: DefaultValues<SavingFormData> = {
  purchaseRequisition: '',
  shoppingCartId: '',
  sourceReferenceItem: '',
  name: '',
  description: '',
  currency: '',
  status: SavingStatus.COMPLETED,
  savingType: null,
  baselinePrice: undefined,
  newPrice: undefined,
  baselineQuantity: undefined,
  newQuantity: undefined,
  originalPriceQuantity: undefined,
  newPriceQuantity: undefined,
  validFrom: null,
  validTo: null,
  procurementOrganizationId: undefined,
  organizationId: undefined,
  commodityGroupId: undefined,
  commodity: '',
  purchasingGroupId: undefined,
  supplierId: undefined,
  accountingAreaId: null,
  fkBuyer: null,
  unitId: null,
  glAccountId: null,
  accountAssignmentCategory: null,
  accountAssignmentValue: null,
  distributionType: null,
  allocationValue: undefined,
  reductionType: null,
};
